import React, { useState, useEffect, useCallback } from 'react';
import Icon from 'components/AppIcon';
import api from 'lib/api';
import { format, differenceInDays, addMonths } from 'date-fns';
import { RadialBarChart, RadialBar, ResponsiveContainer } from 'recharts';

function SkeletonBlock({ className }) {
  return <div className={`bg-[var(--color-muted)] animate-pulse rounded ${className}`} />;
}

const TIMELINE_MAP = {
  '1_month': 1,
  '3_months': 3,
  '6_months': 6,
  '1_year': 12,
};

function getMotivationalMessage(pct) {
  if (pct >= 100) return { msg: 'Goal achieved! 🎉', color: 'var(--color-success)' };
  if (pct >= 76) return { msg: 'Almost there!', color: 'var(--color-primary)' };
  if (pct >= 51) return { msg: 'More than halfway there!', color: 'var(--color-secondary)' };
  if (pct >= 26) return { msg: 'Building momentum', color: 'var(--color-warning)' };
  return { msg: 'Just getting started', color: 'var(--color-muted-foreground)' };
}

export default function GoalProgress({ memberId, memberCreatedAt }) {
  const [goal, setGoal] = useState(null);
  const [measurements, setMeasurements] = useState([]);
  const [workoutLogs, setWorkoutLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUpdateCTA, setShowUpdateCTA] = useState(false);

  const fetchData = useCallback(async () => {
    if (!memberId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      setGoal(null);
      setMeasurements([]);
      setWorkoutLogs([]);
    } catch (e) {
      setError(e?.message ?? 'Failed to load goal data');
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex flex-col gap-6">
        {[1, 2, 3]?.map(i => <SkeletonBlock key={i} className="h-40" />)}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-3 p-4 bg-[var(--color-error)]/5 border border-[var(--color-error)]/20 rounded-[var(--radius-md)]">
        <Icon name="AlertCircle" size={16} color="var(--color-error)" />
        <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{error}</p>
      </div>
    );
  }

  if (!goal) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-20 bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)]">
        <Icon name="Target" size={32} color="var(--color-muted-foreground)" />
        <div className="text-center">
          <h3 className="text-lg font-semibold text-[var(--color-foreground)] font-[var(--font-heading)]">No goal set yet</h3>
          <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1">
            Complete the onboarding flow to set your fitness goal.
          </p>
        </div>
      </div>
    );
  }

  // Timeline calculation
  const startDate = memberCreatedAt ? new Date(memberCreatedAt) : new Date();
  const timelineMonths = TIMELINE_MAP?.[goal?.timeline] ?? 3;
  const targetDate = addMonths(startDate, timelineMonths);
  const today = new Date();
  const totalDays = differenceInDays(targetDate, startDate);
  const elapsedDays = differenceInDays(today, startDate);
  const timelinePct = Math.min(100, Math.max(0, Math.round((elapsedDays / totalDays) * 100)));
  const timelineExpired = today > targetDate;

  // Weight goal progress
  const startWeight = measurements?.[0]?.weight_kg;
  const currentWeight = measurements?.[measurements?.length - 1]?.weight_kg;
  const targetWeight = goal?.target_weight;
  let weightPct = 0;
  if (startWeight && currentWeight && targetWeight) {
    const totalChange = Math.abs(startWeight - targetWeight);
    const actualChange = Math.abs(startWeight - currentWeight);
    weightPct = totalChange > 0 ? Math.min(100, Math.round((actualChange / totalChange) * 100)) : 100;
  }

  // Weekly activity goal (target: 4 days/week)
  const weeklyTarget = 4;
  const uniqueWorkoutDays = new Set(workoutLogs?.map(l => format(new Date(l?.completed_at), 'yyyy-MM-dd')))?.size;
  const weeklyPct = Math.min(100, Math.round((uniqueWorkoutDays / weeklyTarget) * 100));

  // Overall goal completion
  const overallPct = Math.round((weightPct + weeklyPct + timelinePct) / 3);
  const { msg: motivationalMsg, color: msgColor } = getMotivationalMessage(overallPct);

  const radialData = [{ name: 'Weekly Activity', value: weeklyPct, fill: 'var(--color-primary)' }];

  return (
    <div className="flex flex-col gap-6">
      {/* Motivational Banner */}
      <div className="bg-gradient-to-r from-[var(--color-primary)]/10 to-[var(--color-secondary)]/10 border border-[var(--color-primary)]/20 rounded-[var(--radius-md)] p-5">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-[var(--color-primary)]/20 flex items-center justify-center flex-shrink-0">
            <Icon name="Target" size={22} color="var(--color-primary)" />
          </div>
          <div className="flex-1">
            <p className="text-lg font-bold font-[var(--font-heading)]" style={{ color: msgColor }}>{motivationalMsg}</p>
            <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">
              Goal: <span className="font-medium text-[var(--color-foreground)]">{goal?.primary_goal?.replace(/_/g, ' ')?.replace(/\b\w/g, c => c?.toUpperCase())}</span>
              {targetWeight && <> · Target: <span className="font-medium text-[var(--color-foreground)]">{targetWeight} kg</span></>}
            </p>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-2xl font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">{overallPct}%</p>
            <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">overall</p>
          </div>
        </div>
      </div>
      {/* Timeline Progress */}
      <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-5 shadow-[var(--shadow-sm)]">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-[var(--color-foreground)] font-[var(--font-heading)]">Goal Timeline</h3>
          {timelineExpired && (
            <button
              onClick={() => setShowUpdateCTA(true)}
              className="flex items-center gap-1 px-3 py-1.5 bg-[var(--color-warning)]/10 text-[var(--color-warning)] rounded-[var(--radius-sm)] text-xs font-medium font-[var(--font-caption)] hover:bg-[var(--color-warning)]/20 transition-colors"
            >
              <Icon name="RefreshCw" size={12} color="currentColor" />
              Update Goal
            </button>
          )}
        </div>
        <div className="relative">
          <div className="h-3 bg-[var(--color-muted)] rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{ width: `${timelinePct}%`, background: 'linear-gradient(90deg, var(--color-primary), var(--color-secondary))' }}
            />
          </div>
          {/* Today marker */}
          <div
            className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-[var(--color-accent)] border-2 border-white rounded-full shadow-md"
            style={{ left: `calc(${Math.min(98, timelinePct)}% - 8px)` }}
            title="Today"
          />
        </div>
        <div className="flex justify-between mt-2">
          <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
            {format(startDate, 'MMM d, yyyy')}
          </span>
          <span className="text-xs font-medium text-[var(--color-foreground)] font-[var(--font-caption)]">
            {elapsedDays} / {totalDays} days
          </span>
          <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
            {format(targetDate, 'MMM d, yyyy')}
          </span>
        </div>
        {showUpdateCTA && (
          <div className="mt-3 p-3 bg-[var(--color-warning)]/5 border border-[var(--color-warning)]/20 rounded-[var(--radius-sm)]">
            <p className="text-sm text-[var(--color-foreground)] font-[var(--font-caption)]">
              Your goal timeline has passed. Visit the onboarding flow to update your goals.
            </p>
          </div>
        )}
      </div>
      {/* Weight Goal Funnel */}
      {startWeight && currentWeight && targetWeight && (
        <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-5 shadow-[var(--shadow-sm)]">
          <h3 className="text-base font-semibold text-[var(--color-foreground)] font-[var(--font-heading)] mb-4">Weight Goal Progress</h3>
          <div className="flex items-center gap-4">
            <div className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full h-12 bg-[var(--color-muted)] rounded-[var(--radius-sm)] flex items-center justify-center">
                <span className="text-sm font-bold text-[var(--color-foreground)] font-[var(--font-data)]">{startWeight} kg</span>
              </div>
              <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Start</span>
            </div>
            <Icon name="ArrowRight" size={16} color="var(--color-muted-foreground)" />
            <div className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full h-12 bg-[var(--color-primary)]/10 border border-[var(--color-primary)]/30 rounded-[var(--radius-sm)] flex items-center justify-center">
                <span className="text-sm font-bold text-[var(--color-primary)] font-[var(--font-data)]">{currentWeight} kg</span>
              </div>
              <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Current</span>
            </div>
            <Icon name="ArrowRight" size={16} color="var(--color-muted-foreground)" />
            <div className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full h-12 bg-[var(--color-success)]/10 border border-[var(--color-success)]/30 rounded-[var(--radius-sm)] flex items-center justify-center">
                <span className="text-sm font-bold text-[var(--color-success)] font-[var(--font-data)]">{targetWeight} kg</span>
              </div>
              <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Target</span>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Progress</span>
              <span className="text-xs font-semibold text-[var(--color-foreground)] font-[var(--font-caption)]">{weightPct}%</span>
            </div>
            <div className="h-2 bg-[var(--color-muted)] rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{ width: `${weightPct}%`, backgroundColor: 'var(--color-success)' }}
              />
            </div>
          </div>
        </div>
      )}
      {/* Weekly Activity Radial */}
      <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-5 shadow-[var(--shadow-sm)]">
        <h3 className="text-base font-semibold text-[var(--color-foreground)] font-[var(--font-heading)] mb-4">Weekly Activity Goal</h3>
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <div className="w-40 h-40 flex-shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <RadialBarChart
                cx="50%" cy="50%"
                innerRadius="60%" outerRadius="90%"
                barSize={14}
                data={[{ value: weeklyPct, fill: 'var(--color-primary)' }]}
                startAngle={90} endAngle={-270}
              >
                <RadialBar background dataKey="value" cornerRadius={8} />
                <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" className="fill-[var(--color-foreground)]" style={{ fontSize: 22, fontWeight: 700, fontFamily: 'var(--font-heading)' }}>
                  {weeklyPct}%
                </text>
              </RadialBarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-[var(--radius-sm)] bg-[var(--color-primary)]/10 flex items-center justify-center">
                <Icon name="Calendar" size={18} color="var(--color-primary)" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">
                  {uniqueWorkoutDays} <span className="text-base font-medium text-[var(--color-muted-foreground)]">/ {weeklyTarget} days</span>
                </p>
                <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">this week</p>
              </div>
            </div>
            <div className="flex gap-1">
              {Array.from({ length: weeklyTarget }, (_, i) => (
                <div
                  key={i}
                  className={`flex-1 h-2 rounded-full ${i < uniqueWorkoutDays ? 'bg-[var(--color-primary)]' : 'bg-[var(--color-muted)]'}`}
                />
              ))}
            </div>
            <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-2">
              {weeklyTarget - uniqueWorkoutDays > 0
                ? `${weeklyTarget - uniqueWorkoutDays} more workout${weeklyTarget - uniqueWorkoutDays > 1 ? 's' : ''} to hit your weekly goal`
                : 'Weekly goal achieved! 🎉'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
